import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MessageCircle, Store as StoreIcon, ShoppingBag, Sparkles } from "lucide-react";

export default function NeniStore() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get(`/n/${slug}`).then(r => setData(r.data)).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <Navigate to="/" replace />;
  if (!data) return <div className="min-h-screen bg-[#F9F9F8] grid place-items-center text-zinc-500">Cargando tienda…</div>;
  const { tienda, productos } = data;
  const color = tienda.color || "#FF3366";
  const wa = (tienda.whatsapp || "").replace(/\D/g, "");
  const waLink = (p) => `https://wa.me/${wa.length === 10 ? "52" + wa : wa}?text=${encodeURIComponent(`Hola ${tienda.nombre_tienda}, quiero pedir: ${p.nombre} ($${p.precio_venta.toFixed(2)})`)}`;

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="neni-store-page">
      <header className="text-white relative overflow-hidden" style={{ background: color }}>
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
          <div className="overline text-white/80 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Tienda NENI · en 90 MINUTOS</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-2" data-testid="neni-store-name">{tienda.nombre_tienda}</h1>
          {tienda.descripcion && <p className="text-white/85 mt-3 max-w-xl text-sm md:text-base">{tienda.descripcion}</p>}
          <div className="text-xs text-white/70 mt-3 font-mono">{window.location.host}/{tienda.slug}</div>
          {wa && <a href={`https://wa.me/${wa.length === 10 ? "52" + wa : wa}`} target="_blank" rel="noreferrer" data-testid="neni-store-wa" className="mt-5 inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-white hover:text-zinc-900"><MessageCircle className="w-4 h-4" /> Escríbeme por WhatsApp</a>}
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <h2 className="font-display font-bold text-2xl tracking-tighter mb-5">Catálogo <span className="text-zinc-400 text-lg ml-2" data-testid="neni-store-count">{productos.length}</span></h2>
        {productos.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">Esta tienda aún no tiene artículos.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {productos.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.4) }} data-testid={`neni-store-prod-${p.id}`} className="card-lift bg-white border border-zinc-200 rounded-sm overflow-hidden flex flex-col">
                <div className="aspect-square bg-zinc-100 overflow-hidden">{p.imagen_url ? <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-300"><ShoppingBag className="w-8 h-8" /></div>}</div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="overline text-zinc-400">{p.categoria}</div>
                  <div className="font-display font-semibold tracking-tight line-clamp-1">{p.nombre}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5"><StoreIcon className="w-3 h-3" /> {p.tienda?.nombre_local}{p.tienda?.ciudad ? ` · ${p.tienda.ciudad}` : ""}</div>
                  <div className="mt-auto pt-3 flex items-end justify-between gap-2">
                    <span className="font-display font-black text-xl" style={{ color }}>${p.precio_venta.toFixed(2)}</span>
                    {wa ? <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`neni-pedir-${p.id}`} className="text-xs bg-zinc-900 text-white px-3 h-9 grid place-items-center rounded-sm hover:bg-emerald-500">Pedir</a> : <Link to={`/producto/${p.id}`} className="text-xs underline">Ver</Link>}
                  </div>
                  {p.stock_disponible === 0 && <div className="text-[11px] text-red-600 mt-1">Agotado</div>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        <div className="mt-12 text-center text-xs text-zinc-400">Tienda creada con <Link to="/nenis-place" className="underline">NENIS PLACE</Link> · Productos de comercios afiliados a en 90 MINUTOS</div>
      </main>
    </div>
  );
}
